
import { ChangeEvent, useRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Upload, X, GripVertical, Loader2 } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PreloadedImage } from '@/hooks/usePropertyForm';
import { convertHeicFiles, hasAnyHeic, formatHeicErrorMessage } from '@/utils/heicConversion';
import { FieldError } from './FieldError';


const MAX_IMAGES = 90;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];

const SortableImageItem = ({ id, index, url, onRemove }: SortableImageItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative aspect-square bg-muted rounded-md overflow-hidden group"
    >
      <img
        src={url}
        alt={`Property image ${index + 1}`}
        className="w-full h-full object-cover"
      />
      <div
        {...attributes}
        {...listeners}
        className="absolute top-1 left-1 bg-black/50 hover:bg-black/70 text-white p-1 rounded cursor-move opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical className="h-4 w-4" />
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 bg-black/50 hover:bg-black/70 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X className="h-4 w-4" />
      </button>
      {index === 0 && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs py-1 text-center">
          Featured Image
        </div>
      )}
    </div>
  );
};

interface PropertyImagesSectionProps {
  description: string;
  setDescription: (value: string) => void;
  imagePreviewUrls: string[];
  selectedImages: PreloadedImage[];
  handleImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
  removeImage: (index: number) => void;
  reorderImages: (fromIndex: number, toIndex: number) => void;
  errors?: Record<string, string>;
  clearFieldError?: (field: string) => void;
  uploadProgress?: { completed: number; total: number } | null;
}

interface SortableImageItemProps {
  id: string;
  index: number;
  url: string;
  onRemove: () => void;
}

export const PropertyImagesSection = ({
  description,
  setDescription,
  imagePreviewUrls,
  selectedImages,
  handleImageChange,
  removeImage,
  reorderImages,
  errors = {},
  clearFieldError,
  uploadProgress = null,
}: PropertyImagesSectionProps) => {

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isConvertingHeic, setIsConvertingHeic] = useState<{ done: number; total: number } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = imagePreviewUrls.findIndex((_, i) => `image-${i}` === active.id);
      const newIndex = imagePreviewUrls.findIndex((_, i) => `image-${i}` === over.id);
      reorderImages(oldIndex, newIndex);
    }
  };

  const validateAndProcessFiles = (files: File[]): File[] => {
    const remainingSlots = MAX_IMAGES - selectedImages.length;
    
    if (files.length === 0) return [];
    
    // Check if adding these images would exceed the limit
    if (files.length > remainingSlots) {
      alert(`You can only upload ${remainingSlots} more image${remainingSlots !== 1 ? 's' : ''}. Maximum ${MAX_IMAGES} images per listing.`);
      return files.slice(0, remainingSlots);
    }
    
    // Validate file types and sizes
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];
    const oversizedFiles: string[] = [];
    
    files.forEach(file => {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        invalidFiles.push(file.name);
      } else if (file.size > MAX_FILE_SIZE) {
        oversizedFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });
    
    // Show error messages for invalid files
    if (invalidFiles.length > 0) {
      alert(`The following files are not valid image formats:\n${invalidFiles.join(', ')}\n\nPlease upload PNG, JPG, or GIF files only.`);
    }
    
    if (oversizedFiles.length > 0) {
      alert(`The following files exceed the 10MB size limit:\n${oversizedFiles.join(', ')}\n\nPlease use smaller images.`);
    }
    
    return validFiles;
  };

  const handleFileSelection = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    let newFiles = Array.from(e.target.files);

    // Transparently convert iPhone HEIC/HEIF photos to JPEG before validation.
    if (hasAnyHeic(newFiles)) {
      setIsConvertingHeic({ done: 0, total: newFiles.filter((f) => /\.(heic|heif)$/i.test(f.name) || f.type.startsWith('image/heic') || f.type.startsWith('image/heif')).length });
      try {
        const { files: converted, errors } = await convertHeicFiles(newFiles, (done, total) => {
          setIsConvertingHeic({ done, total });
        });
        newFiles = converted;
        if (errors.length > 0) {
          alert(`Could not convert these HEIC files:\n${formatHeicErrorMessage(errors)}`);
        }
      } finally {
        setIsConvertingHeic(null);
      }
    }

    const validFiles = validateAndProcessFiles(newFiles);

    if (validFiles.length === 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Create a new synthetic event with validated files
    const dataTransfer = new DataTransfer();
    validFiles.forEach(file => dataTransfer.items.add(file));

    const syntheticEvent = {
      target: { files: dataTransfer.files }
    } as ChangeEvent<HTMLInputElement>;

    handleImageChange(syntheticEvent);
    clearFieldError?.("images");

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set isDragging to false if we're leaving the drop zone entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    let files = Array.from(e.dataTransfer.files);

    if (hasAnyHeic(files)) {
      setIsConvertingHeic({ done: 0, total: files.filter((f) => /\.(heic|heif)$/i.test(f.name) || f.type.startsWith('image/heic') || f.type.startsWith('image/heif')).length });
      try {
        const { files: converted, errors } = await convertHeicFiles(files, (done, total) => {
          setIsConvertingHeic({ done, total });
        });
        files = converted;
        if (errors.length > 0) {
          alert(`Could not convert these HEIC files:\n${formatHeicErrorMessage(errors)}`);
        }
      } finally {
        setIsConvertingHeic(null);
      }
    }

    const validFiles = validateAndProcessFiles(files);

    if (validFiles.length === 0) return;

    // Create a new synthetic event with validated files
    const dataTransfer = new DataTransfer();
    validFiles.forEach(file => dataTransfer.items.add(file));

    const syntheticEvent = {
      target: { files: dataTransfer.files }
    } as ChangeEvent<HTMLInputElement>;

    handleImageChange(syntheticEvent);
    clearFieldError?.("images");
  };

  const remainingSlots = MAX_IMAGES - selectedImages.length;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Description & Images</h2>
            <div className="text-sm text-muted-foreground">
              {selectedImages.length} / {MAX_IMAGES} images
            </div>
          </div>
          <Separator />

          {uploadProgress && (
            <div className="rounded-md border-2 border-godyspo-gold bg-godyspo-gold/10 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-godyspo-gold" />
                <div className="flex-1">
                  <p className="font-medium text-foreground">
                    Uploading {uploadProgress.completed} of {uploadProgress.total} photos…
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Please keep this tab open. Do not refresh or navigate away.
                  </p>
                </div>
              </div>
              <Progress
                value={uploadProgress.total > 0 ? (uploadProgress.completed / uploadProgress.total) * 100 : 0}
              />
            </div>
          )}


          
          <div className="space-y-2">
            <Label htmlFor="description">
              Property Description <span className="text-muted-foreground text-xs">(Optional)</span>
            </Label>
            <Textarea 
              id="description" 
              placeholder="Enter details about the property..." 
              className="min-h-[150px] bg-muted"
              value={description}
              onChange={(e) => setDescription(e.target.value)} 
            />
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="images">
                Property Images <span className="text-destructive">*</span>
              </Label>
              {remainingSlots > 0 && (
                <span className="text-sm text-muted-foreground">
                  {remainingSlots} slot{remainingSlots !== 1 ? 's' : ''} remaining
                </span>
              )}
            </div>
            
            {remainingSlots > 0 ? (
              <div 
                className={`border-2 border-dashed rounded-md p-6 text-center transition-colors duration-200 ${
                  isDragging 
                    ? 'border-primary bg-primary/5' 
                    : errors.images
                      ? 'border-destructive'
                      : 'border-border'
                }`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  id="images"
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileSelection}
                />
                <label
                  htmlFor="images"
                  className="cursor-pointer flex flex-col items-center justify-center"
                >
                  <div className="flex flex-col items-center justify-center">
                    <Upload
                      className={`mx-auto h-12 w-12 transition-colors duration-200 ${
                        isDragging ? 'text-primary' : 'text-muted-foreground'
                      }`}
                    />
                    <div className="flex text-sm text-muted-foreground">
                      <span className="relative rounded-md font-medium text-primary hover:text-primary/90">
                        Upload files
                      </span>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    {isConvertingHeic && (
                      <p className="text-xs text-primary mt-2">
                        Converting iPhone photos… {isConvertingHeic.done}/{isConvertingHeic.total}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      PNG, JPG, GIF up to 10MB each • Max {MAX_IMAGES} images per listing
                    </p>
                  </div>
                </label>
              </div>
            ) : (
              <div className="border-2 border-dashed border-border rounded-md p-6 text-center bg-muted/50">
                <div className="flex flex-col items-center justify-center">
                  <Upload className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
                  <p className="text-sm text-muted-foreground mt-2">
                    Maximum of {MAX_IMAGES} images reached
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Remove existing images to upload more
                  </p>
                </div>
              </div>
            )}
            <FieldError message={errors.images} />
            
            
            {/* Image Preview Section */}
            {imagePreviewUrls.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">Selected Images ({imagePreviewUrls.length})</h3>
                  <span className="text-xs text-muted-foreground">Drag to reorder</span>
                </div>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={imagePreviewUrls.map((_, i) => `image-${i}`)}
                    strategy={rectSortingStrategy}
                  >
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {imagePreviewUrls.map((url, index) => (
                        <SortableImageItem
                          key={`image-${index}`}
                          id={`image-${index}`}
                          index={index}
                          url={url}
                          onRemove={() => removeImage(index)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
